import { useEffect, useMemo, useState } from 'react';
import { HttpService, IHttpService } from '../../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../../services/notifierService';
import { useForm } from 'react-hook-form';
import { IServiceShareInputCreateRequestModel } from '../../../../models/serviceShareInput/ServiceShareInputCreateRequestModel';
import { serviceShareInputCreateRequestModelValidationSchema } from '../validations/serviceShareInputCreateRequestModelValidationSchema';
import { yupResolver } from '@hookform/resolvers/yup';

export const useAddServiceShareInput = (triggerRefetch: () => void) => {
	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const [isInputDialogVisible, setIsInputDialogVisible] =
		useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const {
		control,
		handleSubmit,
		watch,
		trigger,
		reset,
		formState: { isValid, errors, isSubmitSuccessful }
	} = useForm<IServiceShareInputCreateRequestModel>({
		defaultValues: {
			distributorOrEntity: undefined,
			numberOfEmployees: 0,
			numberOfTechnicians: 0,
			fridgeTechnicians: undefined,
			channel: undefined,
			countryId: undefined,
			quarter: undefined,
			year: undefined,
			brandId: undefined
		},
		resolver: yupResolver(serviceShareInputCreateRequestModelValidationSchema()),
		mode: 'onChange',
		reValidateMode: 'onChange'
	});

	const formValues = watch();

	useEffect(() =>{
		trigger();
	}, [formValues.numberOfEmployees, formValues.numberOfTechnicians])

	const onSubmitRequest = async (): Promise<void> => {
		trigger();

		if(!isValid){
			return;
		}

		setIsLoading(true);

		try {
			await httpService.post(
				'serviceShareInputs/create',
				JSON.stringify(formValues),
				undefined,
				false
			);

			triggerRefetch();

			reset();
			setIsInputDialogVisible(false);
		} catch (err) {
			notifierService.error("Cannot process the request. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	const onDialogVisibleChange = (isVisible: boolean) => {
		setIsInputDialogVisible(isVisible);

		reset();
	}

	return {
		control,
		isInputDialogVisible,
		onDialogVisibleChange,
		isLoading,
        handleSubmitRequest: handleSubmit(onSubmitRequest),
	};
};
